import React, { useEffect, useRef, useState } from "react";
import { Modal } from "../../UI/DesignStandards";
import { createLeagueSheetSignedUrl } from "../../../utility/averageCatalogs";
import { displaySheetPage } from "../../../utility/sheetHighlight";
import { SheetRowPreview, isPdfFile } from "./SheetRowPreview";

const LeagueSheetViewer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  storagePath,
  mimeType,
  fileName,
  sourcePage,
  boundingBox,
  highlight,
  highlightAverage = null,
  highlightGames = null,
  highlightPins = null,
  createSignedUrl = createLeagueSheetSignedUrl,
}) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [paneWidth, setPaneWidth] = useState(0);
  const paneRef = useRef(null);
  const pageNumber = displaySheetPage(sourcePage);
  const pdf = isPdfFile(mimeType, fileName);
  const rowView = Boolean(highlight || sourcePage != null || boundingBox);

  useEffect(() => {
    let cancelled = false;
    if (!isOpen || !storagePath) {
      setUrl("");
      setError(storagePath ? "" : "This league sheet file is not stored.");
      return undefined;
    }
    setError("");
    setUrl("");
    createSignedUrl(storagePath)
      .then((signedUrl) => {
        if (!cancelled) setUrl(signedUrl);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setError(err.message || "Could not open this league sheet.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, storagePath, createSignedUrl]);

  useEffect(() => {
    if (!paneRef.current) return undefined;
    const pane = paneRef.current;
    const updateWidth = () => {
      const styles = window.getComputedStyle(pane);
      const pad =
        (parseFloat(styles.paddingLeft) || 0) +
        (parseFloat(styles.paddingRight) || 0);
      setPaneWidth(Math.max(0, (pane.clientWidth || 0) - pad));
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [url, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      maxWidth="max-w-7xl"
      className="h-[95vh] !max-h-[95vh] sm:h-[95vh] sm:!max-h-[95vh]"
      bodyClassName="!overflow-hidden flex flex-col"
    >
      {error ? (
        <p className="text-sm text-brand-charcoal">{error}</p>
      ) : !url ? (
        <p className="text-sm text-brand-muted">Opening league sheet.</p>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col gap-3">
          {highlight ? (
            <p className="text-sm text-brand-charcoal shrink-0">{highlight}</p>
          ) : null}
          {pageNumber ? (
            <p className="text-sm text-brand-muted shrink-0">Page {pageNumber}</p>
          ) : null}
          {pdf && !rowView ? (
            <div className="flex-1 min-h-0 h-0 overflow-hidden overscroll-contain">
              <iframe
                title={title || "League sheet"}
                src={url}
                className="w-full h-full border border-brand-charcoal/10 rounded-lg bg-white"
              />
            </div>
          ) : (
            <div
              ref={paneRef}
              className="flex-1 min-h-0 h-0 overflow-hidden border border-brand-charcoal/10 rounded-lg bg-brand-cream p-3"
            >
              <SheetRowPreview
                url={url}
                storagePath={storagePath}
                mimeType={mimeType}
                fileName={fileName}
                sourcePage={sourcePage}
                boundingBox={boundingBox}
                highlightName={title}
                highlightAverage={highlightAverage}
                highlightGames={highlightGames}
                highlightPins={highlightPins}
                containerWidth={paneWidth}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default LeagueSheetViewer;
